import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  useDownloadPreview,
  useFlowBillingRuns,
  useReportPreview,
  useRunNow,
  useRunReport,
  useSendRun,
  useTestSendFlowBilling,
} from '../../../hooks/admin/useFlowBilling'
import { useAuth } from '../../../hooks/useAuth'
import { LoadingSpinner } from '../../../shared/components/ui/LoadingSpinner'
import { formatInTimezone, formatVolume } from '../../../utils/formatInTimezone'
import { EMAIL_REGEX } from './RecipientsSection'
import { Card } from './fields'
import { monthLabel, previousMonth, shiftMonth } from './nextRun'
import { RunActions, StatusPill } from './RunActions'
import type {
  FlowBillingConfig,
  FlowBillingRun,
  PreviewComparison,
  PreviewResult,
  ReportDevice,
  ReportPeriod,
  ReportRow,
  ReportTotals,
} from '../../../types/flowBilling'

/**
 * Reports ▸ Flowmeter ▸ Preview.
 *
 * One month at a time, top to bottom:
 *
 * 1. The month, and whether it has been generated.
 * 2. Its versions — the live preview plus every stored run — any of which can
 *    be opened, downloaded, published, unpublished or deleted here, without
 *    going to History.
 * 3. The group's devices, each with a tick box. Unticking one recomputes the
 *    preview without it, and Generate produces exactly what is shown.
 * 4. The report being viewed, with its actions beneath it.
 *
 * Generating and sending are separate steps. Generate stores a new version and
 * is only offered when the preview differs from every version already stored —
 * an identical one would add nothing. Send, beside the month's status, emails
 * the newest unsent version. The scheduled job does both at once, and is what
 * will eventually make this routine.
 *
 * When the month already has a report and the preview differs from it, the
 * difference is stated and left to the administrator. A device missing from an
 * old month may well have been a test unit then — regenerating would be wrong,
 * and only a person knows which it is.
 */

type Month = { year: number; month: number }

/** Which version is on screen: the live preview, or a stored run by id. */
type View = 'preview' | number

/** How far back the stepper goes. Far enough to reconcile a previous season. */
const MONTHS_BACK = 36

const BUTTON =
  'px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50'

export function ReportPreviewSection({
  config,
  dirty,
}: {
  config: FlowBillingConfig
  /** Unsaved settings on another tab. A run uses the stored configuration. */
  dirty: boolean
}) {
  const latest = previousMonth(config.timezone)
  const earliest = shiftMonth(latest, -MONTHS_BACK)
  const [period, setPeriod] = useState<Month>(latest)
  const [view, setView] = useState<View>('preview')
  // Unticked devices, by serial number. Every device starts in the report;
  // leaving one out is a deliberate act for this month, not a standing setting.
  const [excluded, setExcluded] = useState<number[]>([])

  const goTo = (next: Month) => {
    setPeriod(next)
    setView('preview')
    setExcluded([])
  }

  const preview = useReportPreview(config.locationId ? period : null, excluded)
  // placeholderData keeps the last answer on screen while the next one loads;
  // it must not be read as this month's.
  const live = preview.data && !preview.isPlaceholderData ? preview.data : null
  const lastLive = preview.data ?? null

  const versionsQuery = useFlowBillingRuns(
    { ...period, includeDeleted: true, limit: 100 },
    Boolean(config.locationId)
  )
  const versions = versionsQuery.data?.runs ?? []
  const selectedRun = typeof view === 'number' ? versions.find((r) => r.id === view) ?? null : null
  const stored = useRunReport(selectedRun ? selectedRun.id : null)

  const [dialog, setDialog] = useState<'send' | 'test' | null>(null)
  const runNow = useRunNow()
  const sendRun = useSendRun()
  const download = useDownloadPreview()

  // What Send would email: the version being viewed if it can be sent,
  // otherwise the newest version produced since the month was last sent.
  const newestFirst = [...versions].sort((a, b) => b.attempts - a.attempts)
  const lastSent = newestFirst.find((r) => r.status === 'sent') ?? null
  const canSend = (r: FlowBillingRun) => r.status === 'generated' || r.status === 'published'
  const sendTarget =
    selectedRun && canSend(selectedRun)
      ? selectedRun
      : newestFirst.find((r) => canSend(r) && (!lastSent || r.attempts > lastSent.attempts)) ?? null

  const atLatest = period.year === latest.year && period.month === latest.month
  const atEarliest = period.year === earliest.year && period.month === earliest.month
  const label = monthLabel(period)
  const existing = live?.existing ?? null
  const params = { ...period, excludeSerials: excluded }
  // An empty group cannot produce a report worth storing, downloading or
  // emailing, so every action is withheld until it has a device.
  const noDevices = Boolean(lastLive) && (lastLive?.devices ?? []).length === 0

  if (!config.locationId) {
    return (
      <Card title="Preview">
        <p className="text-sm text-gray-600">Choose a group under Settings first.</p>
      </Card>
    )
  }

  return (
    <>
      <Card title="Report">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous month"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40"
              disabled={atEarliest}
              onClick={() => goTo(shiftMonth(period, -1))}
            >
              ‹
            </button>
            <span className="w-40 text-center text-lg font-semibold text-gray-900">{label}</span>
            <button
              type="button"
              aria-label="Next month"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40"
              disabled={atLatest}
              onClick={() => goTo(shiftMonth(period, 1))}
            >
              ›
            </button>
          </div>
          {live && <StatusLine existing={existing} timezone={live.period.timezone} />}
          <button
            type="button"
            className="ml-auto px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
            disabled={!sendTarget || sendRun.isPending}
            title={
              sendTarget
                ? `Email version ${sendTarget.attempts} to every enabled recipient`
                : lastSent
                  ? 'The newest version has been sent. Generate a new one to send it.'
                  : 'Generate a version first'
            }
            onClick={() => setDialog('send')}
          >
            {sendRun.isPending ? 'Sending…' : sendTarget ? `Send version ${sendTarget.attempts}…` : 'Send…'}
          </button>
        </div>

        <VersionsTable
          versions={versions}
          view={view}
          onView={setView}
          decimalPlaces={config.decimalPlaces}
        />
      </Card>

      {preview.isError && view === 'preview' && (
        <p className="text-sm text-red-600">
          The preview could not be produced. {(preview.error as any)?.response?.data?.error?.message}
        </p>
      )}

      {view === 'preview' && !lastLive && preview.isFetching && (
        <Card title="Preview">
          <Loading text={`Computing the ${label} preview…`} />
        </Card>
      )}

      {view === 'preview' && lastLive && (
        <>
          {live && existing && <ChangeNotice existing={existing} preview={live} />}
          <DevicesCard
            devices={lastLive.devices ?? []}
            excluded={excluded}
            onToggle={(serial) =>
              setExcluded((prev) =>
                prev.includes(serial) ? prev.filter((s) => s !== serial) : [...prev, serial].sort((a, b) => a - b)
              )
            }
          />
          <Card
            title={
              <span className="flex items-center gap-2">
                Preview
                <span className="text-sm font-normal text-gray-500">
                  computed now, not saved
                </span>
                {preview.isFetching && (
                  <span className="flex items-center gap-1.5 text-sm font-normal text-gray-500">
                    <LoadingSpinner size="sm" />
                    updating…
                  </span>
                )}
              </span>
            }
          >
            <ReportBody
              period={lastLive.period}
              groupName={lastLive.groupName}
              totals={lastLive.totals}
              unit={lastLive.unit}
              decimalPlaces={lastLive.decimalPlaces}
              rows={lastLive.rows}
              notice={lastLive.notice}
            />
            {lastLive.truncated && (
              <p className="mt-2 text-xs text-gray-500">
                Showing the first {lastLive.rows.length}. The download contains all {lastLive.rowCount}.
              </p>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4">
              <button
                type="button"
                className={BUTTON}
                disabled={!live || noDevices || download.isPending}
                onClick={() => download.mutate({ ...params, format: 'pdf' })}
              >
                Download PDF
              </button>
              <button
                type="button"
                className={BUTTON}
                disabled={!live || noDevices || download.isPending}
                onClick={() => download.mutate({ ...params, format: 'csv' })}
              >
                Download CSV
              </button>
              <button
                type="button"
                className={BUTTON}
                disabled={!live || noDevices}
                onClick={() => setDialog('test')}
              >
                Send test…
              </button>

              <div className="ml-auto flex flex-wrap items-center gap-3">
                {noDevices ? (
                  <span className="text-xs text-gray-500">
                    The group has no devices, so there is nothing to generate.
                  </span>
                ) : dirty ? (
                  <span className="text-xs text-gray-500">Save your settings before generating.</span>
                ) : (
                  live?.identicalTo && (
                    <span className="text-xs text-gray-500">
                      Same as version {live.identicalTo.attempts} ({live.identicalTo.status}) — nothing new
                      to generate.
                    </span>
                  )
                )}
                {/* Only when there is something new: no report yet, or figures
                    that differ from every stored version. The server refuses a
                    duplicate too; this just does not offer one. */}
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                  title="Stores this report as a new version. Nobody is emailed — use Send for that."
                  disabled={!live || noDevices || dirty || Boolean(live.identicalTo) || runNow.isPending}
                  onClick={() => runNow.mutate({ ...params, sendEmail: false })}
                >
                  {runNow.isPending ? 'Generating…' : 'Generate'}
                </button>
              </div>
            </div>
          </Card>
        </>
      )}

      {selectedRun && (
        <Card
          title={
            <span className="flex items-center gap-2">
              Version {selectedRun.attempts}
              <StatusPill status={selectedRun.status} />
            </span>
          }
        >
          {stored.isLoading && <Loading text="Loading this version…" />}
          {stored.data && (
            <ReportBody
              period={stored.data.period}
              groupName={stored.data.groupName}
              totals={stored.data.totals}
              unit={stored.data.unit}
              decimalPlaces={stored.data.decimalPlaces}
              rows={stored.data.rows}
              notice={stored.data.notice}
              generatedAt={stored.data.generatedAt}
              reason={selectedRun.reason}
            />
          )}
          <div className="mt-6 flex items-center gap-2 border-t border-gray-200 pt-4 text-sm">
            <RunActions run={selectedRun} />
            <span className="ml-3 text-xs text-gray-500">
              The stored version, exactly as {selectedRun.sentAt ? 'sent' : 'generated'}. It is never
              recomputed.
            </span>
          </div>
        </Card>
      )}

      {dialog === 'test' && (
        <TestSendDialog
          params={params}
          label={label}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'send' && sendTarget && (
        <ConfirmSendDialog
          label={label}
          run={sendTarget}
          lastSent={lastSent}
          pending={sendRun.isPending}
          onCancel={() => setDialog(null)}
          onConfirm={(reason) => {
            sendRun.mutate({ runId: sendTarget.id, reason })
            setDialog(null)
          }}
        />
      )}
    </>
  )
}

function Loading({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 py-8 text-sm text-gray-500">
      <LoadingSpinner />
      {text}
    </div>
  )
}

const STATUS_STYLE: Record<string, string> = {
  sent: 'bg-green-50 text-green-700',
  published: 'bg-green-50 text-green-700',
  generated: 'bg-blue-50 text-blue-700',
  failed: 'bg-red-50 text-red-700',
}

/** Whether this month has a report, and what the group can see. */
function StatusLine({ existing, timezone }: { existing: PreviewComparison | null; timezone: string }) {
  if (!existing) {
    return (
      <span className="rounded px-2 py-0.5 text-sm bg-gray-100 text-gray-700">Not generated yet</span>
    )
  }

  const { run } = existing
  const when = (value: string | null) => (value ? formatInTimezone(value, timezone) : '')
  const text: Record<string, string> = {
    sent: `Sent ${when(run.sentAt)}`,
    published: 'Published to the portal, not emailed',
    generated: 'Generated — not yet published or sent, so the group cannot see it',
    failed: 'The last attempt failed',
  }

  return (
    <span className="flex items-center gap-2 text-sm text-gray-600">
      <span className={`rounded px-2 py-0.5 ${STATUS_STYLE[run.status] ?? 'bg-gray-100 text-gray-700'}`}>
        {run.status}
      </span>
      {text[run.status]}
    </span>
  )
}

/**
 * What the district gets of a version, mirroring the API's rules: a sent or
 * published version is the one on their Reports page; a superseded one is off
 * the list but still opens from the revision note on its replacement; anything
 * else (a generated working copy, a failure, a delete) they never see.
 */
function DistrictVisibility({ status }: { status: FlowBillingRun['status'] }) {
  const [text, className, title] =
    status === 'sent' || status === 'published'
      ? ['Listed', 'text-green-700 font-medium', "This is the version on the district's Reports page"]
      : status === 'superseded'
        ? ['By link only', 'text-gray-600', 'Off their list, but it opens from the revision note on the version that replaced it']
        : ['Hidden', 'text-gray-400', 'The district cannot see this version']
  return (
    <span className={`whitespace-nowrap text-xs ${className}`} title={title}>
      {text}
    </span>
  )
}

/**
 * The live preview and every stored version of the month, one per row. Picking
 * a row shows that version below; the stored ones carry their own actions.
 * Deleted versions are folded away until asked for.
 */
function VersionsTable({
  versions,
  view,
  onView,
  decimalPlaces,
}: {
  versions: FlowBillingRun[]
  view: View
  onView: (view: View) => void
  decimalPlaces: number
}) {
  const [showDeleted, setShowDeleted] = useState(false)
  const deleted = versions.filter((r) => r.status === 'deleted').length
  const shown = useMemo(
    () =>
      versions
        .filter((r) => showDeleted || r.status !== 'deleted' || r.id === view)
        .sort((a, b) => b.attempts - a.attempts),
    [versions, showDeleted, view]
  )

  const radio = (value: View, text: ReactNode) => (
    <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap">
      <input type="radio" name="report-version" checked={view === value} onChange={() => onView(value)} />
      {text}
    </label>
  )

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['Version', 'Status', 'District sees', 'Generated', 'Sent', 'Total', ''].map((h, i) => (
              <th
                key={i}
                className={`px-3 py-1.5 text-xs font-medium text-gray-500 ${i === 5 ? 'text-right' : 'text-left'}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          <tr className={view === 'preview' ? 'bg-blue-50/50' : ''}>
            <td className="px-3 py-1.5">{radio('preview', 'Live preview')}</td>
            <td className="px-3 py-1.5 text-gray-500" colSpan={6}>
              Computed now from the group as it is, with the devices ticked below — the district never sees this
            </td>
          </tr>
          {shown.map((run) => (
            <tr key={run.id} className={view === run.id ? 'bg-blue-50/50' : ''}>
              <td className="px-3 py-1.5">
                {radio(run.id, `Version ${run.attempts}`)}
                {run.reason && (
                  <div className="max-w-xs truncate pl-6 text-xs text-gray-500" title={run.reason}>
                    {run.reason}
                  </div>
                )}
              </td>
              <td className="px-3 py-1.5">
                <StatusPill status={run.status} />
              </td>
              <td className="px-3 py-1.5">
                <DistrictVisibility status={run.status} />
              </td>
              <td className="px-3 py-1.5 whitespace-nowrap text-gray-700">
                {run.generatedAt ? formatInTimezone(run.generatedAt, run.timezone) : '—'}
              </td>
              <td className="px-3 py-1.5 whitespace-nowrap text-gray-700">
                {run.sentAt ? formatInTimezone(run.sentAt, run.timezone) : '—'}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
                {formatVolume(run.totalVolume, run.decimalPlaces ?? decimalPlaces)}
              </td>
              <td className="px-3 py-1.5 whitespace-nowrap text-right">
                <RunActions run={run} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {deleted > 0 && (
        <button
          type="button"
          className="mt-2 text-xs text-gray-500 hover:text-gray-700 hover:underline"
          onClick={() => setShowDeleted(!showDeleted)}
        >
          {showDeleted ? 'Hide deleted' : `Show deleted (${deleted})`}
        </button>
      )}
    </div>
  )
}

/**
 * How the live preview differs from the month's existing report. Rendered only
 * when it does; a month that still matches says nothing beyond its status.
 */
function ChangeNotice({ existing, preview }: { existing: PreviewComparison; preview: PreviewResult }) {
  const { addedDevices, removedDevices, totalChanged, rowCountChanged, run } = existing
  if (run.status === 'failed') return null
  if (addedDevices.length === 0 && removedDevices.length === 0 && !totalChanged && !rowCountChanged) {
    return null
  }

  const device = (d: { ric: string; siteName: string }) => (d.siteName ? `${d.ric} (${d.siteName})` : d.ric)
  const dp = preview.decimalPlaces

  return (
    <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <p className="font-medium">This preview differs from the {run.status} report.</p>
      <ul className="mt-1 list-disc pl-5">
        {addedDevices.length > 0 && (
          <li>In this preview, not in the report: {addedDevices.map(device).join(', ')}</li>
        )}
        {removedDevices.length > 0 && (
          <li>In the report, not in this preview: {removedDevices.map(device).join(', ')}</li>
        )}
        {totalChanged && (
          <li>
            Total is {formatVolume(preview.totals.totalVolume, dp)} {preview.unit} here, and{' '}
            {formatVolume(run.totalVolume, dp)} in the report
          </li>
        )}
        {rowCountChanged && !addedDevices.length && !removedDevices.length && (
          <li>
            {preview.rowCount} flow sensors here, {run.rowCount} in the report
          </li>
        )}
      </ul>
      <p className="mt-2 text-amber-800">
        If a device was a test unit in {preview.period.label}, the report is right as it is — untick
        it below to match. Otherwise, generate a new version and send it.
      </p>
    </div>
  )
}

const DEVICE_STATUS: Record<ReportDevice['status'], { label: string; className: string }> = {
  included: { label: 'In report', className: 'text-green-700' },
  excluded: { label: 'Left out', className: 'text-amber-700' },
  no_readings: { label: 'No readings — left out', className: 'text-gray-500' },
  no_flow_sensor: { label: 'No flow sensor', className: 'text-gray-500' },
}

/**
 * Every device in the group, each with a tick box. Unticking one leaves it out
 * of the preview, the downloads, the test email and whatever is generated.
 * A device with no flow sensor has nothing to include, so it has no box.
 */
function DevicesCard({
  devices,
  excluded,
  onToggle,
}: {
  devices: ReportDevice[]
  excluded: number[]
  onToggle: (serial: number) => void
}) {
  const included = devices.filter((d) => d.status === 'included').length
  return (
    <Card title={`Devices (${included} of ${devices.length} in the report)`}>
      {devices.length === 0 ? (
        <p className="text-sm text-gray-600">The group has no devices.</p>
      ) : (
        <Table
          fullWidth
          head={['RIC', 'Serial', 'Site', 'Flow sensors', '']}
          align={['left', 'right', 'left', 'left', 'left']}
        >
          {devices.map((d) => (
            <tr key={d.serialNumber} className={excluded.includes(d.serialNumber) ? 'text-gray-400' : ''}>
              <td className="px-3 py-1.5">
                {d.ports.length > 0 ? (
                  <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      aria-label={`Include ${d.ric}`}
                      checked={!excluded.includes(d.serialNumber)}
                      onChange={() => onToggle(d.serialNumber)}
                    />
                    {d.ric}
                  </label>
                ) : (
                  // No flow sensor, so nothing to tick: indent past the checkbox to keep the RICs lined up.
                  <span className="whitespace-nowrap pl-6">{d.ric}</span>
                )}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">{d.serialNumber}</td>
              <td className="px-3 py-1.5">{d.siteName}</td>
              <td className="px-3 py-1.5">{d.ports.join(', ') || '—'}</td>
              <td className={`px-3 py-1.5 ${DEVICE_STATUS[d.status].className}`}>
                {DEVICE_STATUS[d.status].label}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  )
}

/** A report as the PDF lays it out: the header block, then one row per flow sensor. */
function ReportBody({
  period,
  groupName,
  totals: t,
  unit,
  decimalPlaces: dp,
  rows,
  notice,
  generatedAt,
  reason,
}: {
  period: ReportPeriod
  groupName: string
  totals: ReportTotals
  unit: string
  decimalPlaces: number
  rows: ReportRow[]
  notice?: string | null
  generatedAt?: string
  reason?: string | null
}) {
  const pairs: Array<[string, string]> = [
    ['Period start', period.startLocal],
    ['Period end', period.endLocal],
    ['Timezone', period.timezone],
    ['Group', groupName],
    ['Unit', unit],
    ...(generatedAt ? ([['Generated', generatedAt]] as Array<[string, string]>) : []),
    ...(reason ? ([['Reason', reason]] as Array<[string, string]>) : []),
  ]

  return (
    <>
      <dl className="grid grid-cols-[8rem_1fr] gap-y-0.5 text-sm mb-4">
        {pairs.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-gray-500">{k}</dt>
            <dd className="text-gray-900">{v}</dd>
          </div>
        ))}
      </dl>

      {rows.length === 0 ? (
        // The reason the sent file gives, so the preview shows what the district will read.
        <p className="text-sm font-medium text-gray-900">{notice || 'No flow sensor reported in this period.'}</p>
      ) : (
        <Table
          head={['Serial', 'Site', 'Port', 'Product', `Volume (${unit})`]}
          align={['right', 'left', 'center', 'left', 'right']}
        >
          {rows.map((row) => (
            <tr key={`${row.serialNumber}-${row.port}`}>
              <td className="px-3 py-1.5 text-right tabular-nums">{row.serialNumber}</td>
              <td className="px-3 py-1.5">{row.siteName}</td>
              <td className="px-3 py-1.5 text-center">{row.port}</td>
              <td className="px-3 py-1.5">{row.sensorProduct}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{formatVolume(row.totalVolume, dp)}</td>
            </tr>
          ))}
        </Table>
      )}
    </>
  )
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' }

/** Content-width by default: a wide page should not stretch five short columns apart. `fullWidth` fills the card. */
function Table({
  head,
  align,
  fullWidth = false,
  children,
}: {
  head: string[]
  align: Array<keyof typeof ALIGN>
  fullWidth?: boolean
  children: ReactNode
}) {
  return (
    <div className="overflow-x-auto">
      <table className={fullWidth ? 'w-full text-sm' : 'text-sm'}>
        <thead className="bg-gray-50">
          <tr>
            {head.map((h, i) => (
              <th key={i} className={`px-3 py-1.5 text-xs font-medium text-gray-500 ${ALIGN[align[i]]}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">{children}</tbody>
      </table>
    </div>
  )
}

function Dialog({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {children}
      </div>
    </div>
  )
}

/** Email the preview, with its device choices, to one address. Records nothing. */
function TestSendDialog({
  params,
  label,
  onClose,
}: {
  params: Month & { excludeSerials: number[] }
  label: string
  onClose: () => void
}) {
  const { user } = useAuth()
  const [to, setTo] = useState('')
  const testSend = useTestSendFlowBilling()
  // Defaulted to whoever is signed in, but editable: proving delivery usually
  // means sending to a colleague or the district's own contact.
  useEffect(() => {
    if (!to && user?.email) setTo(user.email)
  }, [user?.email, to])
  const valid = EMAIL_REGEX.test(to.trim())

  return (
    <Dialog title={`Send a test of ${label}`}>
      <p className="text-sm text-gray-600 mt-2">
        Emails the report to this address only. Nothing is recorded and no recipient is contacted.
      </p>
      <input
        type="email"
        aria-label="Send test to"
        className="mt-4 w-full border border-gray-300 rounded px-3 py-2 text-sm"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        autoFocus
      />
      <div className="flex justify-end gap-3 mt-6">
        <button type="button" className={BUTTON} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50"
          disabled={!valid || testSend.isPending}
          onClick={() => testSend.mutate({ ...params, to: to.trim() }, { onSuccess: onClose })}
        >
          {testSend.isPending ? 'Sending…' : 'Send test'}
        </button>
      </div>
    </Dialog>
  )
}

/**
 * Confirm emailing a stored version to every recipient. A month that has
 * already been sent gets it as a revision, which needs a reason: without one a
 * corrected invoice is indistinguishable from a duplicate.
 */
function ConfirmSendDialog({
  label,
  run,
  lastSent,
  pending,
  onCancel,
  onConfirm,
}: {
  label: string
  run: FlowBillingRun
  lastSent: FlowBillingRun | null
  pending: boolean
  onCancel: () => void
  onConfirm: (reason: string | null) => void
}) {
  const [reason, setReason] = useState('')
  const revision = lastSent !== null

  return (
    <Dialog title={`Send version ${run.attempts} of ${label}?`}>
      {revision ? (
        <>
          <p className="text-sm text-gray-600 mt-2">
            Version {lastSent.attempts} was already sent
            {lastSent.sentAt ? ` on ${formatInTimezone(lastSent.sentAt, lastSent.timezone)}` : ''}. The
            recipients will get version {run.attempts} as a revision of it. The original stays
            downloadable.
          </p>
          <label className="block mt-4">
            <span className="text-sm font-medium text-gray-700">Reason (shown to the recipients)</span>
            <input
              type="text"
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm"
              maxLength={256}
              autoFocus
              placeholder="RIC1030_0611 added to the group"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        </>
      ) : (
        <p className="text-sm text-gray-600 mt-2">
          Emails version {run.attempts} to every enabled recipient and makes it the report the district
          sees for {label}.
        </p>
      )}
      <div className="flex justify-end gap-3 mt-6">
        <button type="button" className={BUTTON} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
          disabled={pending || (revision && reason.trim() === '')}
          onClick={() => onConfirm(revision ? reason.trim() : null)}
        >
          Send
        </button>
      </div>
    </Dialog>
  )
}
